import Button from 'components/Buttons/Button';
import Render from 'components/Common/Render';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckmarkCircle20Filled, DismissCircleFilled, Info20Filled } from '@fluentui/react-icons';
import { PlanActivationStatus } from 'enum/common';
import { amountOptions } from 'constants/amountOptions';
import moment from 'moment';
import useMixpanel from 'hooks/useMixpanel';
import { routes } from 'constants/routes';
import ListYourBusinessPromotionDetails from 'components/Home/PromotionPlans/ListYourBusinessPromotionDetails';
import AcceptInstantPayPromotionDetails from 'components/Home/PromotionPlans/AcceptInstantPayPromotionDetails';
import OffersPromotionDetails from 'components/Home/PromotionPlans/OffersPromotionDetails';
import DoubleUpPromotionDetails from 'components/Home/PromotionPlans/DoubleUpPromotionDetails';
import SubscriptionsPromotionDetails from 'components/Home/PromotionPlans/SubscriptionsPromotionDetails';
import RegisteredBusinessPlans from 'components/Home/RegisteredBusinessPlans';
import PausedOfferStatus from 'components/Home/PausedOfferStatus';
import { fileDownload } from 'utils/common';
import { http } from 'middleware/apiMiddleware';
import { downloadQrApi } from 'constants/apiUrls';
import toast from 'react-hot-toast';

interface IPromotionPlansProps {
  activateOffer: (planId: string, topUpAmount: number, referralCode: string) => void;
  deActivateOffer: (busienssPlan: BusinessPlanType) => void;
  updateTopupAmount: (planId: string, topUpAmount: number) => void;
  plans: BusinessPlanType[];
  isGlobal?: boolean;
  isUpdatingAmount?: boolean;
  isAnyWhereCommunity?: boolean;
  business: BusinessType;
  errorMessage: string;
  setErrorMessage: (value: string) => void;
  activateInstantPay: (moduleId: string) => void;
  activateSubscription: (moduleId: string) => void;
  showRegisteredBusinessCard?: boolean;
}

const PromotionPlans: React.FC<IPromotionPlansProps> = ({
  activateOffer,
  deActivateOffer,
  updateTopupAmount,
  plans,
  isGlobal = true,
  isUpdatingAmount,
  isAnyWhereCommunity,
  business,
  errorMessage,
  setErrorMessage,
  activateInstantPay,
  activateSubscription,
  showRegisteredBusinessCard = false,
}) => {
  const [topUpAmount, setTopupAmount] = useState(
    (!!plans?.length && plans[0]?.formData?.autoTopUpAmount) || amountOptions[0]?.value,
  );
  const [referralCode, setReferralCode] = useState(
    business.usedReferralCode || (!!plans?.length && plans[0]?.formData?.referralCode) || '',
  );
  const { trackEvent } = useMixpanel();
  const navigate = useNavigate();
  const [allowTopupUpdate, setAllowTopupUpdate] = useState(false);

  const getDefaultValue = useCallback(() => {
    const defaultAmount = !!plans?.length && plans[0]?.formData?.autoTopUpAmount;
    return (
      amountOptions?.filter((amount) => amount?.value === defaultAmount)[0] || amountOptions[0]
    );
  }, [topUpAmount, plans?.length]);

  const renderOfferStatus = (status: PlanActivationStatus) => {
    switch (status) {
      // Not using This Status When status is INITIATED Will show NOT ACTIVE.
      // case PlanActivationStatus.INITIATED:
      //   return (
      //     <div className="text-darkBlue text-base flex gap-1 flex-row items-center">
      //       <CheckmarkCircle20Filled color="green" className="mr-1 mb-[2px]" />
      //       <span className="text-black opacity-60"> Status: </span>
      //       <span className="text-green font-medium">Initiated</span>
      //     </div>
      //   );
      case PlanActivationStatus.ACTIVATED:
        return (
          <div className="text-darkBlue text-base flex gap-1 flex-row items-center">
            <CheckmarkCircle20Filled color="green" className="mr-1 mb-[2px]" />
            <span className="text-black opacity-60">Offer Status: </span>
            <span className="text-green font-medium">Active</span>
          </div>
        );
      case PlanActivationStatus.PENDING:
        return (
          <div className="text-darkBlue text-base flex gap-1 flex-row items-center">
            <CheckmarkCircle20Filled color="orange" className="mr-1 mb-[2px]" />
            <span className="text-black opacity-60"> Status: </span>
            <span className="text-yellow font-medium">Pending</span>
          </div>
        );
      case PlanActivationStatus.FAILED:
        return (
          <div className="text-darkBlue flex-1">
            <DismissCircleFilled className="mr-1 mb-[2px] text-failed" />
            <span className="text-black">Status: </span>
            <span className="text-failed font-medium">FAILED</span>
          </div>
        );
      case PlanActivationStatus.EXPIRED:
        return (
          <div className="text-darkBlue flex-1 ">
            <DismissCircleFilled className="mr-1 mb-[2px] text-failed" />
            <span className="text-black">Status: </span>
            <span className="text-failed font-medium">Not Active</span>
          </div>
        );
      case PlanActivationStatus.DE_ACTIVATED:
        return (
          <div className="text-darkBlue flex-1 ">
            <DismissCircleFilled className="mr-1 mb-[2px] text-failed" />
            <span className="text-black">Status: </span>
            <span className="text-failed font-medium">Not Active</span>
          </div>
        );
      case PlanActivationStatus.PAUSED:
        return (
          <div className="text-darkBlue text-base flex gap-1 flex-row items-center">
            <PausedOfferStatus />
          </div>
        );
      default:
        return (
          <div className="text-darkBlue flex-1 ">
            <DismissCircleFilled className="mr-1 mb-[2px] text-failed" />
            <span className="text-black">Status: </span>
            <span className="text-failed font-medium">Not Active</span>
          </div>
        );
    }
  };

  const RenderPromotionalDetailsContainer = (plan: BusinessPlanType) => {
    if (plan?.module.label === 'List your business on Open Rewards') {
      return (
        <ListYourBusinessPromotionDetails
          plan={plan}
          isGlobal={isGlobal}
          allowTopupUpdate={allowTopupUpdate}
          getDefaultValue={getDefaultValue}
          setTopupAmount={setTopupAmount}
          setReferralCode={setReferralCode}
          referralCode={referralCode}
          error={errorMessage}
          setErrorMessage={setErrorMessage}
        />
      );
    } else if (plan?.module.label === 'Instant Pay') {
      return <AcceptInstantPayPromotionDetails />;
    } else if (plan?.module.label === 'Offers') {
      return <OffersPromotionDetails />;
    } else if (plan?.module.label === 'Double-up') {
      return <DoubleUpPromotionDetails />;
    } else if (plan?.module.label === 'Subscription') {
      return <SubscriptionsPromotionDetails />;
    }
  };

  const handleUpdateAmount = (planId: string = '') => {
    trackEvent('update_button_Clicking', { planId, topUpAmount });
    if (topUpAmount > 0 && allowTopupUpdate) {
      updateTopupAmount(planId || '', topUpAmount);
      setAllowTopupUpdate(false);
    } else {
      setAllowTopupUpdate(true);
      setTopupAmount(0);
    }
  };

  const handleActivateOffer = (plan: BusinessPlanType) => {
    if (plan?.module.label === 'List your business on Open Rewards') {
      trackEvent('activate_offer_button_Clicking', {
        plan: plan?.uuid,
        topUpAmount,
      });
      if (topUpAmount > 0) {
        activateOffer(plan?.uuid || '', topUpAmount, referralCode);
      }
    } else if (plan?.module.label === 'Instant Pay') {
      activateInstantPay(plan?.uuid || '');
    } else if (plan?.module.label === 'Subscription') {
      activateSubscription(plan?.uuid || '');
    } else if (plan?.module.label === 'Offers') {
      // NO-OP
    } else if (plan?.module.label === 'Double-up') {
      // NO-OP
    }
  };

  const [downloadingQr, setDownloadingQr] = useState(false);
  const downloadQR = async () => {
    try {
      setDownloadingQr(true);
      const { data, error } = await http.get(downloadQrApi(business?.uuid));
      if (error) {
        toast.error('Unexpected Error occured while generating qrcode');
      }

      if (data) fileDownload(data?.entityQrCode);
    } catch (error) {
      toast.error('Unexpected Error occured while generating qrcode');
    } finally {
      setDownloadingQr(false);
    }
  };

  const isFreeTrialActive = (trialActive?: boolean, trialEndDate?: string) => {
    if (!trialActive) return false;

    const currentDate = moment();
    const trialEnd = moment(trialEndDate);
    return trialActive || !trialEnd.isBefore(currentDate);
  };

  const filteredPlans = () => {
    // for business belonging to Anywhere Community, we will show all the plans
    // but for thw business belonging to Community, we will not show "List your business on Open Rewards"
    // plan as that is by default active for all the businesses
    return isAnyWhereCommunity
      ? plans
      : plans?.filter((plan) => plan?.module?.label !== 'List your business on Open Rewards');
  };

  const listYourBusinessPlan = plans?.find(
    (plan) => plan?.module?.label === 'List your business on Open Rewards',
  );

  const getClassName = (plan: BusinessPlanType) => {
    return isFreeTrialActive(plan?.module?.isActive, plan?.module?.trialEndDate)
      ? 'line-through'
      : '';
  };

  const isActivateButtonDisabled = (plan: BusinessPlanType) =>
    plan?.status === PlanActivationStatus.ACTIVATED ||
    plan?.status === PlanActivationStatus.PENDING ||
    plan?.status === PlanActivationStatus.PAUSED ||
    !plan?.module?.isActive;

  const isPlanPaused = (plan: BusinessPlanType) => plan?.status === PlanActivationStatus.PAUSED;

  const canDeactivatePlan = (plan: BusinessPlanType) =>
    plan?.status === PlanActivationStatus.ACTIVATED || plan?.status === PlanActivationStatus.PAUSED;

  const showActivateButton = (plan: BusinessPlanType) =>
    plan?.status !== PlanActivationStatus.ACTIVATED && plan?.status !== PlanActivationStatus.PAUSED;

  return (
    <div className="overflow-visible">
      {/* showing List your business card active for the registered community businesses */}
      {!isAnyWhereCommunity && showRegisteredBusinessCard && (
        <div className="mb-4">
          <RegisteredBusinessPlans
            businessStatus={business?.businessStatus}
            planStatus={listYourBusinessPlan?.status}
          />
        </div>
      )}

      {filteredPlans()?.map((plan) => (
        <div
          key={plan?.uuid}
          className="bg-white  p-4 mb-3 gap-4 flex flex-col rounded-sm overflow-visible"
        >
          <div className="flex md:flex-row flex-col"></div>
          <div className="flex flex-col md:flex-row ">
            <div className="flex flex-row flex-1 md:mb-0 mb-4 items-center">
              <div className="flex-col">
                <div className="text-navyBlue text-xl font-semibold flex-1">
                  {plan?.module?.label}
                </div>
                {plan?.module?.isActive && (
                  <div className="flex-1 mt-1">{renderOfferStatus(plan?.status)}</div>
                )}
              </div>
            </div>
            {plan?.module?.isActive && plan?.module?.label !== 'Subscription' && (
              <div className="flex flex-col">
                <Render when={plan?.module?.label === 'Instant Pay'}>
                  <span className="text-brightOrange text-xl font-semibold">FREE</span>
                </Render>
                <Render when={plan?.module?.label !== 'Instant Pay'}>
                  <span className="text-navyBlue font-semibold text-xl ">
                    <span className={getClassName(plan)}>${plan?.module?.price}</span>
                    <span className="text-navyBlue font-semibold text-sm"> {`/month`}</span>
                  </span>{' '}
                  {isFreeTrialActive(plan?.module?.trialActive, plan?.module?.trialEndDate) &&
                    plan?.module?.trialEndDate && (
                      <div className="text-yellow">
                        <Info20Filled className="mr-1 mb-[3px]" />
                        <span className="font-semibold text-l">
                          FREE until {moment.utc(plan?.module?.trialEndDate).format('Do MMM YYYY')}
                        </span>
                      </div>
                    )}
                </Render>
              </div>
            )}
          </div>

          {RenderPromotionalDetailsContainer(plan)}

          <Render when={plan?.module.label === 'Instant Pay'}>
            <div className="flex justify-end md:flex-row flex-col gap-4">
              <Render when={plan?.status === PlanActivationStatus.ACTIVATED}>
                <Button
                  loading={downloadingQr}
                  label={'QR Code'}
                  type={'button'}
                  disabled={downloadingQr}
                  customClass={'md:!w-auto'}
                  onClick={downloadQR}
                />
                <Button
                  label={'Deactivate'}
                  type={'button'}
                  variant="secondary"
                  disabled={!canDeactivatePlan(plan)}
                  customClass={'md:!w-auto !bg-failed !bg-white !text-failed !border-failed'}
                  onClick={() => deActivateOffer(plan)}
                />
              </Render>
              <Render when={isPlanPaused(plan)}>
                <Button
                  label={'Deactivate'}
                  type={'button'}
                  variant="secondary"
                  customClass={'md:!w-auto !bg-failed !bg-white !text-failed !border-failed'}
                  onClick={() => deActivateOffer(plan)}
                />
              </Render>
              <Render when={showActivateButton(plan)}>
                <Button
                  label={plan?.module?.isActive ? 'Activate' : 'COMING SOON!'}
                  type={'button'}
                  disabled={isActivateButtonDisabled(plan)}
                  customClass={'md:!w-auto'}
                  onClick={() => {
                    handleActivateOffer(plan);
                  }}
                />
              </Render>
            </div>
          </Render>

          <Render when={plan?.module.label === 'Subscription'}>
            <div className="flex justify-end md:flex-row flex-col gap-4">
              <Render when={plan?.status === PlanActivationStatus.ACTIVATED}>
                <Button
                  label={'Manage subscriptions'}
                  type={'button'}
                  variant="secondary"
                  customClass={'md:!w-auto !mb-0'}
                  onClick={() => {
                    navigate(routes.subscriptionTiers);
                  }}
                />
                <Button
                  label={'Deactivate'}
                  type={'button'}
                  variant="secondary"
                  disabled={!canDeactivatePlan(plan)}
                  customClass={'md:!w-auto !mb-0 !bg-failed !bg-white !text-failed !border-failed'}
                  onClick={() => deActivateOffer(plan)}
                />
              </Render>
              <Render when={isPlanPaused(plan)}>
                <Button
                  label={'Deactivate'}
                  type={'button'}
                  variant="secondary"
                  customClass={'md:!w-auto !mb-0 !bg-failed !bg-white !text-failed !border-failed'}
                  onClick={() => deActivateOffer(plan)}
                />
              </Render>
              <Render when={showActivateButton(plan)}>
                <Button
                  label={plan?.module?.isActive ? 'Activate' : 'COMING SOON!'}
                  type={'button'}
                  disabled={isActivateButtonDisabled(plan)}
                  customClass={'md:!w-auto'}
                  onClick={() => {
                    handleActivateOffer(plan);
                  }}
                />
              </Render>
            </div>
          </Render>

          <Render
            when={
              isGlobal &&
              plan?.module.label !== 'Instant Pay' &&
              plan?.module.label !== 'Subscription'
            }
          >
            <div className="flex justify-end md:flex-row flex-col gap-4">
              <Render when={plan?.status === PlanActivationStatus.ACTIVATED}>
                <Button
                  label={allowTopupUpdate ? 'Save' : 'Update'}
                  type={'button'}
                  loading={isUpdatingAmount}
                  customClass={'md:!w-auto'}
                  onClick={() => handleUpdateAmount(plan?.uuid)}
                />
                <Button
                  label={'Deactivate'}
                  type={'button'}
                  variant="secondary"
                  disabled={!canDeactivatePlan(plan)}
                  customClass={'md:!w-auto !bg-failed !bg-white !text-failed !border-failed'}
                  onClick={() => deActivateOffer(plan)}
                />
              </Render>
              <Render when={isPlanPaused(plan)}>
                <Button
                  label={'Deactivate'}
                  type={'button'}
                  variant="secondary"
                  customClass={'md:!w-auto !bg-failed !bg-white !text-failed !border-failed'}
                  onClick={() => deActivateOffer(plan)}
                />
              </Render>
              <Render when={showActivateButton(plan)}>
                <Button
                  label={plan?.module?.isActive ? 'Activate' : 'COMING SOON!'}
                  type={'button'}
                  disabled={isActivateButtonDisabled(plan)}
                  customClass={'md:!w-auto'}
                  onClick={() => {
                    handleActivateOffer(plan);
                  }}
                />
              </Render>
            </div>
          </Render>
        </div>
      ))}
    </div>
  );
};

export default PromotionPlans;
